/**
 * Swiper init for homepage partials (theme only — not Gutenberg blocks).
 * Relies on Swiper 10 loaded globally as `swiper-js` in functions.php.
 */

const SWIPER_BASE = {
	slidesPerView: 'auto',
	loop: false,
	keyboard: {
		enabled: true,
	},
	// Slides = liens : pas de role="group" ni de libellé « 1 / 6 » qui masquerait le titre
	a11y: {
		slideRole: null,
		slideLabelMessage: null,
	},
};

function getSwiper() {
	return typeof window !== 'undefined' ? window.Swiper : undefined;
}

function initActusSliders() {
	const Swiper = getSwiper();
	if (!Swiper) {
		return;
	}

	document.querySelectorAll('.frge-home-actus').forEach((root) => {
		const swiperEl = root.querySelector('.swiper-frge-actus');
		if (!swiperEl || swiperEl.swiper) {
			return;
		}

		const swiper = new Swiper(swiperEl, {
			...SWIPER_BASE,
			spaceBetween: 15,
			navigation: {
				nextEl: root.querySelector('.slider-buttons__next'),
				prevEl: root.querySelector('.slider-buttons__prev'),
			},
		});

		swiper.update();
	});
}

const FUNDERS_MARQUEE_SPEED = 0.3; // px per ms
const HOME_MARQUEE_SPEED = 0.4; // px per ms

function initInfiniteMarquee(selector, speed, pauseOnHover = true) {
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	document.querySelectorAll(selector).forEach((marquee) => {
		if (marquee.dataset.marqueeInit === 'true') {
			return;
		}

		const track = marquee.querySelector('[class$="__track"]');
		const originalSet = marquee.querySelector('[class$="__set"]:not([aria-hidden])');

		if (!track || !originalSet) {
			return;
		}

		marquee.dataset.marqueeInit = 'true';

		let offset = 0;
		let paused = false;
		let setWidth = 0;
		let rafId = null;

		const measure = () => {
			setWidth = originalSet.getBoundingClientRect().width;
		};

		const restartCycle = () => {
			offset = 0;
			track.style.transform = 'translate3d(0, 0, 0)';
		};

		const tick = (timestamp) => {
			if (!marquee._lastTick) {
				marquee._lastTick = timestamp;
			}

			const delta = timestamp - marquee._lastTick;
			marquee._lastTick = timestamp;

			if (!paused && setWidth > 0 && !prefersReducedMotion) {
				offset += speed * delta;

				if (offset >= setWidth) {
					restartCycle();
				} else {
					track.style.transform = `translate3d(-${offset}px, 0, 0)`;
				}
			}

			rafId = requestAnimationFrame(tick);
		};

		measure();

		const resizeObserver = typeof ResizeObserver !== 'undefined'
			? new ResizeObserver(measure)
			: null;

		if (resizeObserver) {
			resizeObserver.observe(originalSet);
		} else {
			window.addEventListener('resize', measure);
		}

		if (pauseOnHover) {
			marquee.addEventListener('mouseenter', () => {
				paused = true;
			});
			marquee.addEventListener('mouseleave', () => {
				paused = false;
			});
		}

		if (document.fonts?.ready) {
			document.fonts.ready.then(measure);
		}

		window.addEventListener('load', measure, { once: true });

		if (!prefersReducedMotion) {
			rafId = requestAnimationFrame(tick);
		}

		marquee._marqueeCleanup = () => {
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
			resizeObserver?.disconnect();
		};
	});
}

function initFundersMarquee() {
	initInfiniteMarquee('.frge-home-funders .funders-marquee', FUNDERS_MARQUEE_SPEED);
}

function initHomeMarquee() {
	initInfiniteMarquee('.frge-home-marquee .home-marquee', HOME_MARQUEE_SPEED, false);
}

function initVideosSliders() {
	const Swiper = getSwiper();
	if (!Swiper) {
		return;
	}

	document.querySelectorAll('.frge-home-videos').forEach((root) => {
		const swiperEl = root.querySelector('.swiper-frge-videos');
		if (!swiperEl || swiperEl.swiper) {
			return;
		}

		const swiper = new Swiper(swiperEl, {
			...SWIPER_BASE,
			// L'écart est le margin-right fluide des cartes (_home-videos.scss) ; un spaceBetween s'y ajouterait et le slider défilerait dans le vide
			spaceBetween: 0,
			navigation: {
				nextEl: root.querySelector('.slider-videos-buttons__next'),
				prevEl: root.querySelector('.slider-videos-buttons__prev'),
			},
		});

		swiper.update();
	});
}

// Contenus liés des pages intérieures : carrousel sous 1280 px, rangée de cartes au-delà
function initRelatedPostsSliders() {
	const Swiper = getSwiper();
	if (!Swiper) {
		return;
	}

	const desktop = window.matchMedia('(min-width: 1280px)');

	document.querySelectorAll('.frge-related-posts').forEach((root) => {
		const swiperEl = root.querySelector('.swiper-frge-related');
		if (!swiperEl) {
			return;
		}

		const prevEl = root.querySelector('.frge-related-posts__button--prev');
		const nextEl = root.querySelector('.frge-related-posts__button--next');

		const sync = () => {
			if (desktop.matches) {
				swiperEl.swiper?.destroy(true, true);
				return;
			}

			if (swiperEl.swiper) {
				return;
			}

			new Swiper(swiperEl, {
				...SWIPER_BASE,
				spaceBetween: 16,
				navigation: { nextEl, prevEl },
				// Libellés traduits déjà posés par le gabarit
				a11y: {
					...SWIPER_BASE.a11y,
					prevSlideMessage: prevEl?.getAttribute('aria-label'),
					nextSlideMessage: nextEl?.getAttribute('aria-label'),
				},
			});
		};

		sync();
		desktop.addEventListener('change', sync);
	});
}

function initHomeSliders() {
	initActusSliders();
	initFundersMarquee();
	initHomeMarquee();
	initVideosSliders();
	initRelatedPostsSliders();
}

document.addEventListener('DOMContentLoaded', initHomeSliders);
